import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import WheelPage from './components/WheelPage';
import HomePage from './pages/HomePage';
import CookieBanner from './components/CookieBanner';
import AccessibilityWidget from './components/AccessibilityWidget';
import ErrorBoundary from './components/ErrorBoundary';
import { VARIANTS as VARIANT_KEYS, VARIANT_SLUGS as SHARED_VARIANT_SLUGS } from './config/variantSlugs';
import './App.css';

// Lazy load pages for better performance
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const AllWheelsPage = lazy(() => import('./pages/AllWheelsPage'));
const FAQPage = lazy(() => import('./components/FAQPage'));
const BlogListPage = lazy(() => import('./pages/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Localized homepage - sets language from URL and renders HomePage
const LocalizedHomePage = () => {
  const { lang } = useParams();
  const { changeLanguage } = useLanguage();
  
  useEffect(() => {
    if (lang && lang.length >= 2) {
      changeLanguage(lang);
    }
  }, [lang, changeLanguage]);
  
  return <HomePage />;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

// Loading fallback
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-spinner"></div>
  </div>
);

// All 20 variants - exported for use in other components
export const VARIANTS = ['main', ...VARIANT_KEYS];

// URL slug mappings for SEO-friendly URLs - exported for use in other components
// Canonical source: src/config/variantSlugs.js (also read by generate-sitemap.js)
export const VARIANT_SLUGS = SHARED_VARIANT_SLUGS;

// Helper to get variant from slug - exported for use in other components
export const getVariantFromSlug = (slug) => {
  if (!slug) return 'main';
  
  // Check all language mappings
  for (const langSlugs of Object.values(VARIANT_SLUGS)) {
    for (const [variant, variantSlug] of Object.entries(langSlugs)) {
      if (variantSlug === slug) {
        return variant;
      }
    }
  }
  
  // Fallback: try to match camelCase variant name
  const camelCase = slug.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  if (VARIANTS.includes(camelCase)) {
    return camelCase;
  }
  
  return 'main';
};

// Helper to get the correct slug for a variant in a specific language
export const getSlugForLanguage = (variantKey, lang) => {
  if (variantKey === 'main') return '';
  const langSlugs = VARIANT_SLUGS[lang] || VARIANT_SLUGS['en'];
  return langSlugs[variantKey] || VARIANT_SLUGS['en'][variantKey] || '';
};

// Dynamic route component with redirect support for old URLs
const VariantPage = () => {
  const { lang, variant: slugParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { changeLanguage } = useLanguage();

  // Get the slug from URL
  const slug = slugParam || window.location.pathname.split('/').filter(Boolean).pop() || '';

  // Validate language code
  const validLanguages = ['en', 'es', 'de', 'ja', 'fr', 'pt-BR', 'ru', 'it', 'zh-CN', 'nl', 'pl', 'tr', 'ko', 'ar', 'hi', 'vi', 'th', 'sv', 'cs', 'el', 'id'];
  const currentLang = (lang && validLanguages.includes(lang)) ? lang : 'en';

  // Find the variant key from any slug (supports all languages)
  const variant = getVariantFromSlug(slug);

  // Get the correct slug for this variant in the current language
  const correctSlug = getSlugForLanguage(variant, currentLang);

  // All navigation logic in a single useEffect (React Hooks rules compliant)
  useEffect(() => {
    // 1. Check if variant is invalid (slug provided but variant is 'main')
    if (variant === 'main' && slug !== '') {
      // Invalid variant slug - navigate to 404 page
      navigate('/404/', { replace: true });
      return;
    }

    // 2. Set language context
    if (currentLang && currentLang !== 'en') {
      changeLanguage(currentLang);
    }

    // 3. Ensure URL has trailing slash
    const pathname = location.pathname;
    if (!pathname.endsWith('/')) {
      navigate(pathname + '/', { replace: true });
      return;
    }

    // 4. If the slug in URL doesn't match the correct localized slug, redirect
    if (variant !== 'main' && slug && slug !== correctSlug && correctSlug) {
      const newPath = currentLang === 'en'
        ? `/${correctSlug}/`
        : `/${currentLang}/${correctSlug}/`;

      // Use replace to not pollute history (acts like 301 redirect)
      navigate(newPath, { replace: true });
    }
  }, [slug, correctSlug, currentLang, variant, navigate, location, changeLanguage]);

  return <WheelPage variant={variant} />;
};

function App() {
  // Check if we're in embed mode
  const isEmbed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === 'true';
  
  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className={`App ${isEmbed ? 'embed-mode' : ''}`}>
          <BrowserRouter>
            <ScrollToTop />
            {!isEmbed && <Header />}
            <ErrorBoundary title="Page Error" description="We encountered an error loading this page. Please try again or return to the homepage.">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Homepage with SEO content */}
                <Route path="/" element={<HomePage />} />
                
                {/* All Wheels Directory Page */}
                <Route path="/wheels" element={<AllWheelsPage />} />
                <Route path="/:lang/wheels" element={<AllWheelsPage />} />
                
                {/* English wheel variant routes */}
                <Route path="/yes-no-wheel" element={<WheelPage variant="yesNo" />} />
                <Route path="/name-picker" element={<WheelPage variant="namePicker" />} />
                <Route path="/team-generator" element={<WheelPage variant="teamGenerator" />} />
                <Route path="/number-wheel" element={<WheelPage variant="numberWheel" />} />
                <Route path="/letter-wheel" element={<WheelPage variant="letterWheel" />} />
                <Route path="/color-wheel" element={<WheelPage variant="colorWheel" />} />
                <Route path="/food-wheel" element={<WheelPage variant="foodWheel" />} />
                <Route path="/movie-picker" element={<WheelPage variant="moviePicker" />} />
                <Route path="/game-picker" element={<WheelPage variant="gamePicker" />} />
                <Route path="/giveaway-wheel" element={<WheelPage variant="giveawayWheel" />} />
                <Route path="/truth-or-dare" element={<WheelPage variant="truthOrDare" />} />
                <Route path="/question-wheel" element={<WheelPage variant="questionWheel" />} />
                <Route path="/casino-wheel" element={<WheelPage variant="casinoWheel" />} />
                <Route path="/date-picker" element={<WheelPage variant="datePicker" />} />
                <Route path="/country-wheel" element={<WheelPage variant="countryWheel" />} />
                <Route path="/drinking-game" element={<WheelPage variant="drinkingGame" />} />
                <Route path="/music-wheel" element={<WheelPage variant="musicWheel" />} />
                <Route path="/sports-wheel" element={<WheelPage variant="sportsWheel" />} />
                <Route path="/activity-wheel" element={<WheelPage variant="activityWheel" />} />
                
                {/* Blog routes - English */}
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                
                {/* Blog routes - Localized */}
                <Route path="/:lang/blog" element={<BlogListPage />} />
                <Route path="/:lang/blog/:slug" element={<BlogPostPage />} />
                
                {/* Legal pages - English */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="/faq" element={<FAQPage />} />
                
                {/* Legal pages - Localized */}
                <Route path="/:lang/privacy" element={<PrivacyPage />} />
                <Route path="/:lang/terms" element={<TermsPage />} />
                <Route path="/:lang/cookies" element={<CookiePolicyPage />} />
                <Route path="/:lang/accessibility" element={<AccessibilityPage />} />
                <Route path="/:lang/faq" element={<FAQPage />} />
                
                {/* Localized home page */}
                <Route path="/:lang" element={<LocalizedHomePage />} />
                
                {/* Localized routes - pattern: /:lang/:variant */}
                <Route path="/:lang/:variant" element={<VariantPage />} />

                {/* 404 Not Found page */}
                <Route path="/404" element={<NotFoundPage />} />

                {/* Catch-all for 404 Not Found */}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <Footer />
            <CookieBanner />
            <AccessibilityWidget />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
